import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  useTheme,
  alpha,
  Stack,
  Button,
  LinearProgress,
  Alert,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllInvestments, fetchPreFundInvestments } from '../store/slices/adminSlice';
import { AccountBalance, Payment, CalendarToday, Visibility, FilterList, Clear } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const AdminInvestmentsPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(0);
  
  // Add filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [status, setStatus] = useState('');
  const [startupId, setStartupId] = useState('');
  const [userId, setUserId] = useState('');
  const [investmentId, setInvestmentId] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const { list: investments, count, loading, error } = useSelector(
    (state) => state.admin.allInvestments
  );

  const { 
    list: preFundInvestments, 
    count: preFundCount, 
    loading: preFundLoading, 
    error: preFundError 
  } = useSelector((state) => state.admin.preFundInvestments);

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setMinAmount('');
    setMaxAmount('');
    setPaymentType('');
    setStatus('');
    setStartupId('');
    setUserId('');
    setInvestmentId('');
  };

  useEffect(() => {
    const filters = {
      page: page + 1,
      limit: rowsPerPage,
    };

    if (startDate && endDate) {
      filters.dateRange = `${startDate}-${endDate}`;
    }
    if (minAmount && maxAmount) {
      filters.amountRange = `${minAmount}-${maxAmount}`;
    }
    if (paymentType) filters.paymentType = paymentType;
    if (status) filters.status = status;
    if (startupId) filters.startupId = startupId;
    if (userId) filters.userId = userId;
    if (investmentId) filters.investmentId = investmentId;

    if (activeTab === 0) {
      dispatch(fetchAllInvestments(filters));
    } else {
      dispatch(fetchPreFundInvestments(filters));
    }
  }, [dispatch, page, rowsPerPage, startDate, endDate, minAmount, maxAmount, paymentType, status, startupId, userId, investmentId, activeTab]);

  const getStatusChip = (status) => {
    const statusConfig = {
      PENDING: {
        label: 'Beklemede',
        color: theme.palette.warning.main,
        bgcolor: alpha(theme.palette.warning.main, 0.1),
      },
      APPROVED: {
        label: 'Onaylandı',
        color: theme.palette.success.main,
        bgcolor: alpha(theme.palette.success.main, 0.1),
      },
      REJECTED: {
        label: 'Başarısız',
        color: theme.palette.error.main,
        bgcolor: alpha(theme.palette.error.main, 0.1),
      },
      REFUNDED: {
        label: 'İade Edildi',
        color: theme.palette.error.main,
        bgcolor: alpha(theme.palette.error.main, 0.1),
      },
      WAITING_SENT: {
        label: 'Gönderim Bekleniyor',
        color: theme.palette.info.main,
        bgcolor: alpha(theme.palette.info.main, 0.1),
      },
      WAITING_REFUND: {
        label: 'İade Bekleniyor',
        color: theme.palette.info.main,
        bgcolor: alpha(theme.palette.info.main, 0.1),
      },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    return (
      <Chip
        label={config.label}
        sx={{
          color: config.color,
          bgcolor: config.bgcolor,
          borderRadius: '16px',
          '& .MuiChip-label': {
            px: 2,
            py: 0.5,
            fontWeight: 500,
          },
        }}
      />
    );
  };

  const getPaymentTypeChip = (type) => {
    const typeConfig = {
      BANK_TRANSFER: {
        label: 'Banka Transferi',
        icon: <AccountBalance fontSize="small" />,
      },
      CREDIT_CARD: {
        label: 'Kredi Kartı',
        icon: <Payment fontSize="small" />,
      },
    };

    const config = typeConfig[type];
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {config.icon}
        <Typography variant="body2">{config.label}</Typography>
      </Stack>
    );
  };

  const renderFilters = () => (
    <Card sx={{ mb: 3, p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Filtreler</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<Clear />}
            onClick={handleClearFilters}
            variant="outlined"
            size="small"
          >
            Temizle
          </Button>
          <IconButton onClick={() => setShowFilters(!showFilters)}>
            <FilterList />
          </IconButton>
        </Stack>
      </Stack>

      {showFilters && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Başlangıç Tarihi"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Bitiş Tarihi"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Stack>
          </Grid>
          {activeTab === 0 && (
            <>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Min Tutar"
                    type="number"
                    value={minAmount}
                    onChange={(e) => setMinAmount(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Max Tutar"
                    type="number"
                    value={maxAmount}
                    onChange={(e) => setMaxAmount(e.target.value)}
                    fullWidth
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Ödeme Türü"
                  value={paymentType}
                  onChange={(e) => setPaymentType(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="">Tümü</MenuItem>
                  <MenuItem value="BANK_TRANSFER">Banka Transferi</MenuItem>
                  <MenuItem value="CREDIT_CARD">Kredi Kartı</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Durum"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="">Tümü</MenuItem>
                  <MenuItem value="PENDING">Beklemede</MenuItem>
                  <MenuItem value="APPROVED">Onaylandı</MenuItem>
                  <MenuItem value="FAILED">Başarısız</MenuItem>
                </TextField>
              </Grid>
            </>
          )}
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Startup ID"
                value={startupId}
                onChange={(e) => setStartupId(e.target.value)}
                fullWidth
              />
              <TextField
                label="Kullanıcı ID"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                fullWidth
              />
              <TextField
                label="Yatırım ID"
                value={investmentId}
                onChange={(e) => setInvestmentId(e.target.value)}
                fullWidth
              />
            </Stack>
          </Grid>
        </Grid>
      )}
    </Card>
  );

  const currentLoading = activeTab === 0 ? loading : preFundLoading;
  const currentError = activeTab === 0 ? error : preFundError;
  const currentData = activeTab === 0 ? investments : preFundInvestments;
  const currentCount = activeTab === 0 ? count : preFundCount;

  if (currentError) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 4 }}>
          {currentError}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            mb: 4, 
            color: theme.palette.primary.main,
            fontWeight: 'bold' 
          }}
        >
          Yatırımlar
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(e, newValue) => {
            setActiveTab(newValue);
            setPage(0);
          }}
          sx={{ mb: 3 }}
        >
          <Tab label="Tüm Yatırımlar" />
          <Tab label="Tüm Ön Yatırımlar" />
        </Tabs>

        {renderFilters()}

        <Card sx={{ borderRadius: 2 }}>
          {currentLoading && <LinearProgress />}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Yatırım ID</TableCell>
                  <TableCell>Startup ID</TableCell>
                  <TableCell>Kullanıcı ID</TableCell>
                  {activeTab === 0 && <TableCell>Ödeme Türü</TableCell>}
                  <TableCell>Miktar</TableCell>
                  <TableCell>Tarih</TableCell>
                  {activeTab === 0 && <TableCell>Durum</TableCell>}
                  <TableCell align="right">İşlem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentData?.map((investment) => (
                  <TableRow key={investment.investmentId}>
                    <TableCell>{investment.investmentId.slice(0, 8)}...</TableCell>
                    <TableCell>{investment.startupId.slice(0, 8)}...</TableCell>
                    <TableCell>{investment.userId.slice(0, 8)}...</TableCell>
                    {activeTab === 0 && (
                      <TableCell>
                        {getPaymentTypeChip(investment.paymentType)}
                      </TableCell>
                    )}
                    <TableCell>
                      {Number(investment.amount).toLocaleString('tr-TR')} ₺
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarToday fontSize="small" color="action" />
                        {new Date(parseInt(investment.registerTime)).toLocaleDateString('tr-TR')}
                      </Stack>
                    </TableCell>
                    {activeTab === 0 && (
                      <TableCell>{getStatusChip(investment.status)}</TableCell>
                    )}
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => navigate(`/yatirimlar/detay/${investment.investmentId}`)}
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          '&:hover': {
                            bgcolor: theme.palette.primary.dark,
                          },
                        }}
                      >
                        Detay
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={currentCount || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Sayfa başına satır:"
          />
        </Card>
      </Box>
    </Container>
  );
};

export default AdminInvestmentsPage;